import { type ReactNode } from 'react';
import { AuthProvider as SharedAuthProvider, type NotifyFn } from '@parleyride/auth';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { cleanupAuthState } from './authUtils';
import { useDemo } from '@/contexts/useDemo';
import { DemoAuthProvider } from './DemoAuthProvider';

export function AuthProvider({ children }: { children: ReactNode }) {
  // Both hooks unconditional, above every branch. Nothing below may be skipped.
  const { toast } = useToast();
  const demo = useDemo();

  const notify: NotifyFn = (opts) => {
    toast({
      title: opts.title,
      description: opts.description,
      variant: opts.variant,
    });
  };

  // Swap the COMPONENT TYPE rather than branching inside one component: React
  // unmounts one and mounts the other, which is legal, whereas an early return
  // inside a single component changes its hook count between renders.
  if (demo.isDemoMode) {
    return <DemoAuthProvider>{children}</DemoAuthProvider>;
  }

  return (
    <SharedAuthProvider
      supabase={supabase}
      redirectOrigin={typeof window !== 'undefined' ? window.location.origin : ''}
      notify={notify}
      onSignOut={() => {
        window.location.href = '/';
      }}
      cleanupAuthState={cleanupAuthState}
    >
      {children}
    </SharedAuthProvider>
  );
}
